import axios from "axios";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  deleteAssistant,
  deleteAssistantFunction,
  generatePrompt,
  getAssistantFunctions,
  getText,
} from "../../../services/api/instances/assistantsApi/methods";
import type {
  AssistantFunction,
  AssistantValue,
  GeneratePromptRequest,
} from "../../../services/api/instances/assistantsApi/types";
import type { RootState } from "../../../services/redux/types";
import { addSelectedFieldToFunctions } from "../../../shared/utils/assistantPageFunctions";
import {
  ChatCompletionsMessageObject,
  TChatCompletionRequest,
} from "../../../services/api/instances/completionsApi/types";
import {
  deleteThread,
  getMessagesFromThread,
  getThreadRun,
  postMessageToCompletion,
  postMessageToResponse,
} from "../../../services/api/instances/responseApi/api";
import {
  mergedDialogMessages,
  transformMessagesObject,
} from "../../Dialog/helpers";
import { getUpdatedValue } from "../helpers/getUpdatedValue";
import { editStorageObject } from "../../../app/state/storageObjectSlice/actions/actions";
import {
  clearMessagesAction,
  setAssistantValue,
  setChatHistory,
  setChatLogs,
} from "./slice";
import { refreshStorageObjectVersionAction } from "../../../app/state/storageObjectSlice/actions/versioning-actions";
import { TResponseApiRequestBody } from "../../../services/api/instances/responseApi/types/request";
import { OutputMessageType } from "../../../services/api/instances/responseApi/types/common";
import { getChatLogs } from "../../../common/Chat/helpers/getChatLogs";
import { denormalizeStorageObjects } from "../../../app/state/storageObjectSlice/utils/denormalizeObjectsTree";
import {
  NormalizedStorageObject,
  StorageObject,
} from "../../../services/api/instances/storageApi/types/object_types";
import { getDeltaHistory } from "../helpers/getDeltaHistory";
import i18next from "i18next";
import { getInstructionWithoutHiddenPartWithTrim } from "../helpers/getInstructionWithoutHiddenPart";
import { getSkills } from "../../../services/api/instances/skillsApi/api";
import { getError } from "../../../services/lib/getError";

export const deleteAssistantAction = createAsyncThunk(
  "assistants/deleteAssistant",
  async (id: string, { rejectWithValue }) => {
    try {
      return await deleteAssistant(id);
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const postMessageWithResponsesAction = createAsyncThunk(
  "assistants/postMessageWithResponses",
  async (
    {
      message,
      id,
      threadRef,
    }: { message: string; id: string; threadRef: string },
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const { assistantValue, messages } = (getState() as RootState)
        .assistantsState;
      const { data } = (getState() as RootState).storageObjectState.assistants;
      const selectedObject = data.entities[id];
      if (!assistantValue || !assistantValue.assistant_object) return;
      const { model, thread_id } = assistantValue.assistant_object;
      const postObject: TResponseApiRequestBody = {
        input: message,
        model: null,
        metadata: {
          assistant_ref: selectedObject?.ref,
          thread_ref: threadRef,
        },
      };
      const systemResponse = await postMessageToResponse(postObject);
      const systemMessage = systemResponse?.output.at(-1) as OutputMessageType;

      const newMessages: ChatCompletionsMessageObject[] = [
        ...messages,
        {
          role: "assistant",
          content: systemMessage.content?.at(-1)?.text as string,
        },
      ];

      if (!thread_id || thread_id === "") {
        const newValue = getUpdatedValue(
          assistantValue,
          { thread_id: threadRef },
          {
            ...assistantValue.meta,
            isThreadStarted: true,
          },
        );

        dispatch(
          editStorageObject({
            id: id,
            name: selectedObject?.friendly_name,
            value: newValue,
            service: "assistants",
          }),
        );

        dispatch(setAssistantValue(newValue));
      }

      return newMessages;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getThreadRunAction = createAsyncThunk(
  "assistants/getThreadRun",
  async (
    { thread_ref, id }: { thread_ref: string; id: string },
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      const { data } = (getState() as RootState).storageObjectState.assistants;
      const { chatHistory } = (getState() as RootState).assistantsState;
      const response = await getThreadRun(thread_ref);
      const objects = denormalizeStorageObjects(data.ids, data.entities);
      if (!response.history) {
        dispatch(
          setChatLogs({
            id: id,
            log: i18next.t("agents.processing_request_log"),
          }),
        );
      }
      const historyLength = chatHistory[id] ? chatHistory[id]?.length : 0;
      const newItems = getDeltaHistory(historyLength, response.history);

      if (newItems?.length > 0) {
        const currentHistory = chatHistory[id] ? chatHistory[id] : [];
        const updatedFullHistory = [...currentHistory, ...newItems];
        dispatch(setChatHistory({ id: id, history: updatedFullHistory }));
      }

      const lastInput = response.history?.at(-1)?.input;
      if (!lastInput) return;
      const finalInput = !Array.isArray(lastInput) ? [lastInput] : lastInput;
      if (finalInput) {
        dispatch(
          setChatLogs({
            id: id,
            log: getChatLogs(
              finalInput,
              objects as StorageObject<AssistantValue>[],
            ),
          }),
        );
      }

      return;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getAssistantSkillsAction = createAsyncThunk(
  "assistants/getAssistantSkills",
  async (_, { rejectWithValue }) => {
    try {
      return getSkills();
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const postMessageToAssistantAction = createAsyncThunk(
  "assistants/postMessageToAssistant",
  async (id: string, { getState, dispatch, rejectWithValue }) => {
    try {
      const { assistantValue, messages } = (getState() as RootState)
        .assistantsState;
      const { selectedId, data } = (getState() as RootState).storageObjectState
        .assistants;
      if (!selectedId) return;
      const selectedObject = data.entities[selectedId];
      if (!assistantValue || !assistantValue.assistant_object) return;
      const { model, thread_id } = assistantValue.assistant_object;
      const postObject: TChatCompletionRequest = {
        model: model,
        messages: [...messages],
        metadata: {
          assistant_ref: selectedObject.ref,
          thread_ref: thread_id ?? "",
        },
      };
      const systemResponse = await postMessageToCompletion(postObject);
      const systemMessage = systemResponse?.choices.at(-1);

      const newMessages: ChatCompletionsMessageObject[] = [
        ...messages,
        {
          role: "assistant",
          content: systemMessage?.message.content as string,
        },
      ];

      if (
        assistantValue?.meta?.isThreadStarted === false ||
        assistantValue?.meta?.isThreadStarted === undefined
      ) {
        const newValue = getUpdatedValue(
          assistantValue,
          {},
          {
            ...assistantValue.meta,
            isThreadStarted: true,
          },
        );

        dispatch(
          editStorageObject({
            id: selectedId,
            name: selectedObject?.friendly_name,
            value: newValue,
            service: "assistants",
          }),
        );

        dispatch(setAssistantValue(newValue));
      }

      return newMessages;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getAssistantMessagesAction = createAsyncThunk(
  "assistants/getAssistantMessages",
  async (
    { threadRef, id }: { threadRef: string; id: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const { messages } = (getState() as RootState).assistantsState;
      const responseMessages = await getMessagesFromThread(threadRef);
      const receivedMessages = transformMessagesObject(responseMessages);
      const newMessages = mergedDialogMessages(messages, receivedMessages);
      return newMessages;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteAssistantThreadAction = createAsyncThunk(
  "assistants/deleteAssistantThread",
  async (thread_ref: string, { dispatch, getState, rejectWithValue }) => {
    try {
      const { assistantValue } = (getState() as RootState).assistantsState;
      const { selectedId, data } = (getState() as RootState).storageObjectState
        .assistants;
      if (!selectedId || !assistantValue?.assistant_object) return;
      const selectedObject = data.entities[selectedId];
      if (!selectedObject) return;
      dispatch(clearMessagesAction());

      const newValue: AssistantValue = getUpdatedValue(
        assistantValue,
        {
          thread_id: "",
        },
        {
          isThreadStarted: false,
        },
      );

      dispatch(setAssistantValue(newValue));

      dispatch(
        editStorageObject({
          id: selectedId,
          name: selectedObject?.friendly_name,
          value: newValue,
          service: "assistants",
        }),
      );

      return await deleteThread(thread_ref);
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const migrationAssistantVersionToNewApiAction = createAsyncThunk(
  "assistants/migrationAssistantVersionToNewApi",
  async (
    { value, id }: { value: AssistantValue; id: string },
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const { data, selectedId } = state.storageObjectState.assistants;

      if (!selectedId || !data) return;

      const selectedObject = data.entities[selectedId];
      if (!value.assistant_object || !selectedObject?.value) return;
      const assistantValue = selectedObject.value as AssistantValue;
      const { thread_id } = assistantValue?.assistant_object;

      const newValue: AssistantValue = getUpdatedValue(
        value,
        {
          thread_id: thread_id,
        },
        {
          isLegacyObject: false,
          isThreadStarted: assistantValue?.meta?.isThreadStarted ?? false,
          server_type: "openai",
        },
      );

      dispatch(
        editStorageObject({
          id: selectedId,
          name: selectedObject?.friendly_name,
          value: newValue,
          service: "assistants",
          skipUpdateListener: true,
        }),
      );

      dispatch(
        refreshStorageObjectVersionAction({
          service: "assistants",
          versionId: id,
        }),
      );

      return newValue;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getAssistantFunctionsAction = createAsyncThunk(
  "assistants/getFunctions",
  async ({ id, value }: { id: string; value?: AssistantValue }, thunkAPI) => {
    try {
      const { assistantValue } = (thunkAPI.getState() as RootState)
        .assistantsState;
      const functions = await getAssistantFunctions(id);
      const finalValue = value ? value : assistantValue;
      return addSelectedFieldToFunctions(
        functions,
        finalValue?.assistant_object.tools!,
      );
    } catch (e: unknown) {
      const message = getError(e);
      return thunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteAssistantFunctionAction = createAsyncThunk(
  "assistants/deleteFunction",
  async ({ id, name }: { id: string; name: string }, ThunkApi) => {
    try {
      const { assistantFunctions } = (ThunkApi.getState() as RootState)
        .assistantsState;
      const filteredFunctions: AssistantFunction[] = assistantFunctions.filter(
        (func: AssistantFunction) => func.function_name !== name,
      );
      await deleteAssistantFunction(id, name);

      return filteredFunctions;
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkApi.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const generatePromptAction = createAsyncThunk(
  "assistants/generatePrompt",
  async (_, ThunkAPI) => {
    try {
      const { assistantValue } = (ThunkAPI.getState() as RootState)
        .assistantsState;
      const instruction = getInstructionWithoutHiddenPartWithTrim(
        assistantValue?.assistant_object?.instructions as string,
      );
      const postObj: GeneratePromptRequest = {
        prompt: instruction,
      };

      return await generatePrompt(postObj);
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const uploadFileToAssistantDialogAction = createAsyncThunk(
  "assistants/uploadFileToAssistantDialog",
  async (file: File, { signal, rejectWithValue }) => {
    try {
      return await getText(file, signal);
    } catch (e) {
      if (
        signal.aborted ||
        axios.isCancel(e) ||
        (e instanceof Error && e.name === "AbortError")
      ) {
        return rejectWithValue("Aborted");
      }
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const changeResponseFormatAction = createAsyncThunk(
  "assistants/changeResponseFormat",
  async (id: string, { getState, dispatch, rejectWithValue }) => {
    try {
      const { data } = (getState() as RootState).storageObjectState.assistants;
      const selectedObject = data.entities[
        id
      ] as NormalizedStorageObject<AssistantValue>;
      if (
        !selectedObject ||
        selectedObject?.value === null ||
        selectedObject?.value?.assistant_object?.response_format === "auto"
      )
        return;
      const updatedValue = getUpdatedValue(selectedObject.value, {
        response_format: "auto",
        tools: [{ type: "code_interpreter" }],
        tool_resources: { code_interpreter: { file_ids: [] } },
      });
      dispatch(
        editStorageObject({
          id: id,
          name: selectedObject?.friendly_name,
          value: updatedValue,
          service: "assistants",
        }),
      );
      return updatedValue;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
